import React, { useState } from "react";
import { Modal, Input, Upload, Radio, Alert, Tag, Button, message } from "antd";
import { FileExcelOutlined } from "@ant-design/icons";
import {
  runCitrixSuite,
  parseUIDataset,
  parseUIDatasetFromPath,
} from "../../service/uitest.service";

/**
 * Reusable data-driven run dialog for a CITRIX suite. Used from both the suite
 * list (UIAutomation.js) and the builder (UIAutomationBuilder.js).
 *
 * Dataset source is a choice: upload an .xlsx OR point at a path on the ARC
 * server. Either way ARC parses it, previews "N rows → N runs", and launches
 * one run per row (or a single run when no dataset is attached).
 *
 * Props: open, onClose(), suite (needs suiteId + suiteName).
 */
function CitrixRunModal({ open, onClose, suite }) {
  const [runEnv, setRunEnv] = useState("");
  const [source, setSource] = useState("upload"); // "upload" | "path"
  const [serverPath, setServerPath] = useState("");
  const [dataset, setDataset] = useState(null); // { columns, rows, rowCount, label }
  const [parsing, setParsing] = useState(false);
  const [running, setRunning] = useState(false);

  const reset = () => {
    setRunEnv("");
    setSource("upload");
    setServerPath("");
    setDataset(null);
    setParsing(false);
    setRunning(false);
  };

  const close = () => {
    reset();
    onClose?.();
  };

  const handleUpload = (file) => {
    setParsing(true);
    parseUIDataset(file)
      .then((res) => {
        setDataset({ ...res.data, label: file.name });
        message.success(`Parsed ${res.data.rowCount} row(s)`);
      })
      .catch(() => message.error("Could not parse spreadsheet"))
      .finally(() => setParsing(false));
    return false; // stop AntD auto-upload
  };

  const handleParsePath = () => {
    if (!serverPath.trim()) {
      message.error("Enter a path to the .xlsx on the ARC server");
      return;
    }
    setParsing(true);
    parseUIDatasetFromPath(serverPath.trim())
      .then((res) => {
        setDataset({ ...res.data, label: serverPath.trim() });
        message.success(`Parsed ${res.data.rowCount} row(s)`);
      })
      .catch(() => message.error("Could not read spreadsheet at that path"))
      .finally(() => setParsing(false));
  };

  const submit = () => {
    setRunning(true);
    const body = { environment: runEnv || null, rows: dataset?.rows || null };
    runCitrixSuite(suite.suiteId, body)
      .then((res) => {
        const n = Number(res.data.rows) || 0;
        message.success(
          n > 0
            ? `Queued ${n} run(s) (parent ${res.data.jobId}) — results on the Results page.`
            : `Citrix run started (${res.data.jobId}) — results on the Results page.`,
        );
        close();
      })
      .catch(() => message.error("Failed to start Citrix run"))
      .finally(() => setRunning(false));
  };

  return (
    <Modal
      open={open}
      onCancel={close}
      title={`Run Citrix Suite${suite?.suiteName ? " — " + suite.suiteName : ""}`}
      okText={dataset?.rowCount ? `Run ${dataset.rowCount} row(s)` : "Run"}
      confirmLoading={running}
      onOk={submit}
      centered
      width={560}
      destroyOnClose
    >
      <div style={{ marginTop: 8 }}>
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message="Data-driven run"
          description="Attach a trade spreadsheet to run once per row, or leave it empty for a single run. Each row binds to {{Column}} placeholders in your steps."
        />

        <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 6 }}>
          Environment (optional)
        </div>
        <Input
          value={runEnv}
          onChange={(e) => setRunEnv(e.target.value)}
          placeholder="e.g. PROD"
          style={{ marginBottom: 16 }}
        />

        <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 6 }}>
          Dataset (optional)
        </div>
        <Radio.Group
          value={source}
          onChange={(e) => {
            setSource(e.target.value);
            setDataset(null);
          }}
          style={{ marginBottom: 10 }}
        >
          <Radio value="upload">Upload .xlsx</Radio>
          <Radio value="path">Path on ARC server</Radio>
        </Radio.Group>

        {source === "upload" ? (
          <Upload
            beforeUpload={handleUpload}
            showUploadList={false}
            accept=".xlsx"
            maxCount={1}
          >
            <Button icon={<FileExcelOutlined />} loading={parsing}>
              {dataset ? "Replace spreadsheet" : "Attach spreadsheet"}
            </Button>
          </Upload>
        ) : (
          <Input.Search
            value={serverPath}
            onChange={(e) => setServerPath(e.target.value)}
            onSearch={handleParsePath}
            enterButton="Load"
            loading={parsing}
            placeholder="e.g. C:\ARC_APP\data\trades.xlsx"
          />
        )}

        {dataset && (
          <div
            style={{
              marginTop: 14,
              background: "var(--color-background-secondary, #f5f7fa)",
              border: "1px solid var(--color-border-tertiary, #e4e9f0)",
              borderRadius: 6,
              padding: "10px 12px",
            }}
          >
            <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6 }}>
              <FileExcelOutlined /> {dataset.label} — {dataset.rowCount} row(s) →{" "}
              {dataset.rowCount} run(s)
            </div>
            <div style={{ fontSize: 12, color: "#666" }}>
              Columns:{" "}
              {(dataset.columns || []).map((c) => (
                <Tag key={c} style={{ marginBottom: 4 }}>{`{{${c}}}`}</Tag>
              ))}
            </div>
            {dataset.rowCount === 0 && (
              <div style={{ color: "#A32D2D", fontSize: 12, marginTop: 6 }}>
                No data rows found in the first sheet.
              </div>
            )}
          </div>
        )}
      </div>
    </Modal>
  );
}

export default CitrixRunModal;
